import { all, call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import * as actions from '../actions';
import { apiMethods } from '../services/api';
import { apiMethodsConst } from '../services/methods';
import { NumberUtils } from '~/utils/NumberUtils';
import { IAddress, IUser } from '~/models';

type TApiMethodKey = keyof typeof apiMethods;

export const prepareBodyPS = (
  user: IUser,
  selectedUser?: IUser,
): Record<string, string> => {
  const address: IAddress = user?.address?.[0] || ({} as IAddress);
  const selectedUserAddress: IAddress = selectedUser?.address?.[0] || ({} as IAddress);

  if (selectedUser && user?.id !== selectedUser?._id) {
    return {
      first_name: selectedUser?.first_name || '',
      last_name: selectedUser?.last_name || '',
      full_name: selectedUser?.full_name || '',
      email: selectedUser?.email_id || '',
      formatted_address: selectedUserAddress?.formatted_address || '',
      city: selectedUserAddress?.city || '',
      state: selectedUserAddress?.state || '',
      country: selectedUserAddress?.country || '',
      postal_code: selectedUserAddress?.postal_code || '',
      phone_number: selectedUser?.contact_number || '',
      street_number: selectedUserAddress?.street_number || '',
      street: selectedUserAddress?.street || '',
    };
  }

  return {
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    full_name: user?.full_name || '',
    email: user?.email_id || '',
    formatted_address: address?.formatted_address || '',
    city: address?.city || '',
    state: address?.state || '',
    country: address?.country || '',
    postal_code: address?.postal_code || '',
    phone_number: user?.contact_number || '',
    street_number: address?.street_number || '',
    street: address?.street || '',
  };
};

function* callAndDispatch(method: TApiMethodKey, payload?: any): Generator<any, any, any> {
  const response = yield call(apiMethods[method], payload);
  yield put({ type: method, payload: response });
  return response;
}

function* callAndDispatchPayload(method: TApiMethodKey, payload?: any): Generator<any, any, any> {
  yield call(apiMethods[method], payload);
  yield put({ type: method, payload });
}

function* watchGetAllPaymnets(): Generator<any, void, any> {
  const events: any = actions.createRequestTypes(actions.GET_ALL_PAYMENTS_ACTION);
  while (true) {
    const { officeId } = yield take(actions.GET_ALL_PAYMENTS_ACTION);
    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.GET_ALL_PAYMENTS_REQUEST, { officeId });
      yield put({ type: events.SUCCESS });
    } catch (error: any) {
      yield put({ type: events.FAILURE, payload: error?.response?.data });
    }
  }
}

function* watchGetPlaidToken(): Generator<any, void, any> {
  const events: any = actions.createRequestTypes(actions.GET_PLAID_TOKEN);
  while (true) {
    yield take(actions.GET_PLAID_TOKEN);

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.GET_PLAID_TOKEN, undefined);
      yield put({ type: events.SUCCESS });
    } catch (error: any) {
      yield put({ type: events.FAILURE, payload: error?.response?.data });
    }
  }
}

function* watchSubmitPlaidToken(): Generator<any, void, any> {
  const events: any = actions.createRequestTypes(actions.SUBMIT_PLAID_TOKEN);
  while (true) {
    const {
      publicToken,
      metadata,
      hasAchMandate,
    }: { publicToken: string; metadata: any; hasAchMandate?: boolean } = yield take(
      actions.SUBMIT_PLAID_TOKEN,
    );

    yield put({ type: events.REQUEST });

    const { user, selectedChildOffice }: { user: IUser; selectedChildOffice: IUser } =
      yield select((store: any) => store.user);

    try {
      const response = yield* callAndDispatch(apiMethodsConst.SUBMIT_PLAID_TOKEN, {
        publicToken,
        metadata,
        user: prepareBodyPS(user, selectedChildOffice),
        ...(hasAchMandate && { hasAchMandate }),
      });
      yield* callAndDispatch(apiMethodsConst.GET_ALL_PAYMENTS_REQUEST, undefined);
      yield put({ type: events.SUCCESS, payload: response?.data });
    } catch (error: any) {
      yield put({
        type: events.FAILURE,
        payload: error?.response?.data?.error
          ? { message: error?.response?.data?.error }
          : error?.response?.data,
      });
    }
  }
}

function* watchAddCardPayment(): Generator<any, void, any> {
  const events: any = actions.createRequestTypes(actions.ADD_PAYMENT_ACTION);
  while (true) {
    const { card }: { card: { token?: { id?: string } } } = yield take(actions.ADD_PAYMENT_ACTION);
    yield put({ type: events.REQUEST });
    try {
      const { user }: { user: IUser } = yield select((store: any) => store.user);
      const { selectedChildOffice }: { selectedChildOffice: IUser } = yield select(
        (store: any) => store.user,
      );

      const body = { token: card.token?.id, user: prepareBodyPS(user, selectedChildOffice) };
      yield* callAndDispatch(apiMethodsConst.ADD_PAYMENT_REQUEST, body);

      yield* callAndDispatch(apiMethodsConst.GET_ALL_PAYMENTS_REQUEST, undefined);

      yield put({ type: events.SUCCESS });
    } catch (error: any) {
      yield put({ type: events.FAILURE, payload: error?.response?.data });
    }
  }
}

function* watchDeletePayment(): Generator<any, void, any> {
  const events: any = actions.createRequestTypes(actions.DELETE_PAYMENT_ACTION);
  while (true) {
    const { paymentId }: { paymentId: string } = yield take(actions.DELETE_PAYMENT_ACTION);
    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatchPayload(apiMethodsConst.DELETE_PAYMENT_REQUEST, paymentId);
      yield put({ type: events.SUCCESS });
    } catch (error: any) {
      yield put({ type: events.FAILURE, payload: error?.response?.data });
    }
  }
}

function* watchSelectPayment(): Generator<any, void, any> {
  const events: any = actions.createRequestTypes(actions.SELECT_PAYMENT_ACTION);
  while (true) {
    const { paymentId }: { paymentId: string } = yield take(actions.SELECT_PAYMENT_ACTION);

    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatchPayload(apiMethodsConst.SELECT_PAYMENT_REQUEST, paymentId);

      yield put({ type: events.SUCCESS });
    } catch (error: any) {
      yield put({ type: events.FAILURE, payload: error?.response?.data });
    }
  }
}

function* watchDeleteDefaultPayment(): Generator<any, void, any> {
  const events: any = actions.createRequestTypes(actions.DELETE_DEFAULT_PAYMENT_ACTION);
  while (true) {
    const { oldPaymentId, newPaymentId }: { oldPaymentId: string; newPaymentId: string } =
      yield take(actions.DELETE_DEFAULT_PAYMENT_ACTION);
    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatchPayload(apiMethodsConst.SELECT_PAYMENT_REQUEST, newPaymentId);
      yield* callAndDispatchPayload(apiMethodsConst.DELETE_PAYMENT_REQUEST, oldPaymentId);
      yield put({ type: events.SUCCESS });
    } catch (error: any) {
      yield put({ type: events.FAILURE, payload: error?.response?.data });
    }
  }
}

function* watchVerifyPayment(): Generator<any, void, any> {
  const events: any = actions.createRequestTypes(actions.VERIFY_BANK_ACTION);
  while (true) {
    const { paymentId, data }: { paymentId: string; data: any } = yield take(
      actions.VERIFY_BANK_ACTION,
    );
    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatchPayload(apiMethodsConst.VERIFY_BANK_REQUEST, { paymentId, data });

      yield put({ type: events.SUCCESS });
    } catch (error: any) {
      yield put({ type: events.FAILURE, payload: error?.response?.data });
    }
  }
}

function* watchRequestReport(): Generator<any, void, any> {
  const events: any = actions.createRequestTypes(actions.REQUEST_REPORT_ACTION);

  function* handler({
    payload,
  }: {
    payload: { email: string; ranges: any; notes: string };
  }): Generator<any, void, any> {
    yield put({ type: events.REQUEST });

    try {
      yield* callAndDispatchPayload(apiMethodsConst.REQUEST_PAYMENT_REPORT, {
        email: payload.email,
        dateRange: payload.ranges,
        notes: payload.notes,
      });

      yield put({
        type: actions.SHOW_MESSAGE,
        payload: {
          action: actions.REQUEST_REPORT_ACTION,
          title: 'Request Submitted Successfully!',
          message:
            // eslint-disable-next-line max-len
            `Your transaction report request was submitted successfully. Our team will do our best to return the report within 3-5 business days. If needed sooner, please text ${NumberUtils.getCSPhone().formatted} for assistance.`,
          isSuccess: true,
        },
      });
      yield put({ type: events.SUCCESS });
    } catch (e: any) {
      yield put({ type: events.FAILURE, payload: e });
    }
  }

  yield takeEvery(actions.REQUEST_REPORT_ACTION, handler as any);
}

export default function* sagaPayments(): Generator<any, void, any> {
  yield all([
    fork(watchGetAllPaymnets),
    fork(watchDeletePayment),
    fork(watchAddCardPayment),
    fork(watchSelectPayment),
    fork(watchDeleteDefaultPayment),
    fork(watchVerifyPayment),
    fork(watchGetPlaidToken),
    fork(watchSubmitPlaidToken),
    fork(watchRequestReport),
  ]);
}
